import React, { useCallback } from 'react';
import { Typography, IconButton, Box } from '@mui/material';
import SyncIcon from '@material-ui/icons/Sync';
import Settings from '@material-ui/icons/Settings';
import { useApi, errorApiRef, alertApiRef } from '@backstage/core-plugin-api';
import {
  computeResourceDiscoveryApiRef,
  ExecuteActionAllOptions,
} from '../api';
import { useActionManagement } from './useActionManagement';
import { useRefreshContext } from './RefreshContext';
import {
  CRDAWSConfig,
  CRDAzureConfig,
  CRDGCPConfig,
  CRDKubernetesConfig,
} from '@internal/backstage-plugin-compute-resource-discovery-new-common';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  ProjectCodeActionConfirmationDialog,
  ProjectCodeActionMenu,
} from './actions';

type ProjectCodeWrapperProps = {
  projectCode: string;
  awsConfigs: CRDAWSConfig[];
  gcpConfigs: CRDGCPConfig[];
  azureConfigs: CRDAzureConfig[];
  kubernetesConfigs: CRDKubernetesConfig[];
  children: React.ReactNode;
};

export const ProjectCodeWrapper: React.FC<ProjectCodeWrapperProps> = ({
  projectCode,
  awsConfigs,
  gcpConfigs,
  azureConfigs,
  kubernetesConfigs,
  children,
}) => {
  const { entity } = useEntity<Entity>();
  const api = useApi(computeResourceDiscoveryApiRef);
  const errorApi = useApi(errorApiRef);
  const alertApi = useApi(alertApiRef);
  const { refreshAll } = useRefreshContext();

  const executeAction = useCallback(
    (options: ExecuteActionAllOptions) => {
      return api.executeAllAction(options);
    },
    [api],
  );

  const entityRef = stringifyEntityRef(entity);

  const refresh = useCallback(() => {
    // Invalidate all cache before refreshing
    api.invalidateCache();
    refreshAll();
  }, [api, refreshAll]);

  const {
    menuPosition,
    confirmationDialogOpen,
    selectedAction,
    isActionInProgress,
    handleTableActionClick,
    handleMenuClose,
    handleActionClick,
    handleConfirmAction,
    setConfirmationDialogOpen,
  } = useActionManagement({
    executeAction,
    refresh,
    gcpConfigs,
    awsConfigs,
    azureConfigs,
    kubernetesConfigs,
    errorApi,
    alertApi,
    entityRef,
    projectCode,
  });

  return (
    <>
      <Box sx={{ mb: 2 }}>
        {/* Simple header with title and action buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h3" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
            Project Code: {projectCode}
          </Typography>

          {/* Refresh button */}
          <IconButton
            onClick={refresh}
            aria-label="refresh project resources"
            size="small"
            sx={{ mr: 1 }}
          >
            <SyncIcon />
          </IconButton>

          {/* Settings/Actions button */}
          <IconButton
            onClick={handleTableActionClick}
            aria-label="open actions"
            size="small"
          >
            <Settings />
          </IconButton>
        </Box>

        {/* Content */}
        {children}
      </Box>

      <ProjectCodeActionMenu
        handleActionClick={handleActionClick}
        handleMenuClose={handleMenuClose}
        handleOpenConnection={() => {}}
        menuPosition={menuPosition}
        service="all"
      />

      <ProjectCodeActionConfirmationDialog
        open={confirmationDialogOpen}
        onClose={() => setConfirmationDialogOpen(false)}
        onConfirm={handleConfirmAction}
        action={selectedAction as any}
        isLoading={isActionInProgress}
        service="all"
      />
    </>
  );
};
